import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE } from '../../config';
import { useTheme } from './ThemeContext';

// ============================================================
// TYPES
// ============================================================
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface UploadedFile {
  filename: string;
  original_name: string;
  url: string;
}

interface ChatbotProps {
  user: { id: number; email: string; is_admin: boolean };
  currentPath: string;
}

interface SavedConversation {
  id: string;
  page_path: string;
  page_name: string;
  messages: ChatMessage[];
  session_id: string | null;
  updated_at: number;
}

// ============================================================
// ICON COMPONENTS
// ============================================================
const IconChat = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconX = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconSend = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const IconSparkles = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" />
    <path d="M18 14l.67 2.33L21 17l-2.33.67L18 20l-.67-2.33L15 17l2.33-.67L18 14z" />
  </svg>
);

const IconAttestation = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const IconPaperclip = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const IconFile = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const IconHistory = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconBack = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconTrash = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// ============================================================
// CONVERSATION PERSISTENCE
// ============================================================
const STORAGE_KEY = 'senior_analyst_conversations';

function generateConvId(): string {
  return 'sa_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function loadConversations(): SavedConversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function upsertConversation(
  id: string, page_path: string, page_name: string,
  messages: ChatMessage[], session_id: string | null,
) {
  const all = loadConversations();
  const idx = all.findIndex(c => c.id === id);
  const entry: SavedConversation = { id, page_path, page_name, messages, session_id, updated_at: Date.now() };
  if (idx >= 0) all[idx] = entry; else all.unshift(entry);
  // Keep last 50 conversations
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all.slice(0, 50)));
}

function deleteConversation(id: string) {
  const all = loadConversations().filter(c => c.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

// ============================================================
// PAGE CONTEXT (mirrors Director for page-aware chat)
// ============================================================
function getPageContext(pathname: string): { page_name: string; page_path: string; page_description?: string } {
  if (pathname === '/') return { page_name: 'Dashboard', page_path: pathname, page_description: 'Main dashboard with IARI score, compliance metrics, country of origin distribution, shortage counts, and risk levels.' };
  if (pathname === '/products') return { page_name: 'Products', page_path: pathname, page_description: 'Product database with filters for shortage status, COO confidence, risk level, and BAA eligibility.' };
  if (pathname.startsWith('/products/')) return { page_name: 'Product Detail', page_path: pathname, page_description: 'Detailed view of a single product with scoring, COO analysis, and evidence.' };
  if (pathname === '/manufacturers') return { page_name: 'Manufacturers', page_path: pathname, page_description: 'Manufacturer listing with product counts, recall history, and shortage data.' };
  if (pathname.startsWith('/manufacturers/')) return { page_name: 'Manufacturer Detail', page_path: pathname, page_description: 'Detailed view of a manufacturer with products, CCP affiliation, and leadership.' };
  if (pathname === '/china-dependency') return { page_name: 'China Dependency', page_path: pathname, page_description: 'China dependency analysis showing product and API exposure to Chinese manufacturing.' };
  if (pathname.startsWith('/shortage-warning')) return { page_name: 'Shortage Early Warning', page_path: pathname, page_description: 'Drug shortage monitoring and early warning indicators.' };
  if (pathname === '/self-attestations') return { page_name: 'Self-Attestations', page_path: pathname, page_description: 'Self-attestation claims from manufacturers for country of origin.' };
  if (pathname === '/my-attestations') return { page_name: 'My Attestations', page_path: pathname, page_description: 'Your submitted self-attestation claims and their review status.' };
  if (pathname === '/apis') return { page_name: 'API Intelligence', page_path: pathname, page_description: 'API budget monitoring, usage analytics, and data source health.' };
  return { page_name: 'The Trade Spy', page_path: pathname };
}

// ============================================================
// QUICK ACTIONS PER PAGE
// ============================================================
function getQuickActions(pathname: string): { text: string; query: string; isAttestation?: boolean }[] {
  if (pathname === '/') return [
    { text: 'Product shortages', query: 'What products are currently in shortage?' },
    { text: 'Made in America', query: 'Which products are made in America?' },
    { text: 'Self-attest a product', query: 'I want to self-attest that my product is made in America', isAttestation: true },
  ];
  if (pathname === '/products') return [
    { text: 'High risk products', query: 'Which products have the highest risk profiles?' },
    { text: 'US-origin products', query: 'Which products are confirmed US-origin?' },
    { text: 'Self-attest a product', query: 'I want to self-attest that my product is made in America', isAttestation: true },
  ];
  if (pathname === '/manufacturers' || pathname.startsWith('/manufacturers/')) return [
    { text: 'Manufacturer overview', query: 'Summarize the key data about the manufacturers on this page.' },
    { text: 'Self-attest a product', query: 'I want to self-attest that my product is made in America', isAttestation: true },
  ];
  if (pathname === '/china-dependency') return [
    { text: 'China exposure', query: 'What is our overall exposure to Chinese manufacturing?' },
    { text: 'CCP-affiliated products', query: 'Which products are linked to CCP-affiliated manufacturers?' },
  ];
  if (pathname.startsWith('/shortage-warning')) return [
    { text: 'Active shortages', query: 'What products are currently in shortage?' },
    { text: 'Shortage trends', query: 'What are the current shortage trends?' },
  ];
  return [
    { text: 'What can you help with?', query: 'What kind of analysis can you help me with on this page?' },
    { text: 'Self-attest a product', query: 'I want to self-attest that my product is made in America', isAttestation: true },
  ];
}

// ============================================================
// MARKDOWN RENDERER (same as Director)
// ============================================================
function renderMessage(content: string): React.ReactNode {
  const parts = content.split(/(```[\s\S]*?```)/g);
  return parts.map((part, i) => {
    if (part.startsWith('```')) {
      const code = part.replace(/```\w*\n?/, '').replace(/```$/, '');
      return (
        <pre key={i} style={{
          background: '#1E293B', color: '#E2E8F0', padding: 12,
          borderRadius: 8, fontSize: 12, overflowX: 'auto',
          margin: '8px 0', lineHeight: 1.5,
        }}>
          {code}
        </pre>
      );
    }
    const escaped = part
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return (
      <span key={i} dangerouslySetInnerHTML={{
        __html: escaped
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/`(.*?)`/g, '<code style="background:#F1F5F9;padding:2px 4px;border-radius:3px;font-size:12px">$1</code>')
          .replace(/\n/g, '<br/>')
      }} />
    );
  });
}

// ============================================================
// SENIOR ANALYST COMPONENT
// ============================================================
export default function Chatbot({ user, currentPath }: ChatbotProps) {
  const { T } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [convId, setConvId] = useState<string>(generateConvId());
  const [showHistory, setShowHistory] = useState(false);
  const [allConversations, setAllConversations] = useState<SavedConversation[]>([]);
  const [attestationSubmitted, setAttestationSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [sessionFiles, setSessionFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  // Verification state
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationAttestationId, setVerificationAttestationId] = useState<number | null>(null);
  const [verificationManufacturer, setVerificationManufacturer] = useState<string>('');
  const [verificationStep, setVerificationStep] = useState<'ask' | 'email-input' | 'sending' | 'sent'>('ask');
  const [companyEmail, setCompanyEmail] = useState('');
  const [verificationEmailUsed, setVerificationEmailUsed] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pageContext = getPageContext(currentPath);
  const quickActions = getQuickActions(currentPath);

  // On page navigation: look for existing conversation for this page, or start fresh
  useEffect(() => {
    const saved = loadConversations();
    const existing = saved.find(c => c.page_path === currentPath);
    if (existing && existing.messages.length > 0) {
      setMessages(existing.messages);
      setSessionId(existing.session_id);
      setConvId(existing.id);
    } else {
      setMessages([]);
      setSessionId(null);
      setConvId(generateConvId());
    }
    setShowHistory(false);
    setAttestationSubmitted(false);
    setVerificationRequired(false);
  }, [currentPath]);

  // Persist messages to localStorage
  const persistMessages = useCallback((msgs: ChatMessage[], sid: string | null, cid: string) => {
    if (msgs.length > 0) {
      upsertConversation(cid, currentPath, pageContext.page_name, msgs, sid);
    }
  }, [currentPath, pageContext.page_name]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current && !showHistory) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen, showHistory]);

  // Refresh conversation list when opening history
  useEffect(() => {
    if (showHistory) {
      setAllConversations(loadConversations());
    }
  }, [showHistory]);

  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    // If there are uploaded files, mention them
    let fullText = text;
    const currentFiles = [...uploadedFiles];
    if (currentFiles.length > 0) {
      const fileNames = currentFiles.map(f => f.original_name).join(', ');
      fullText = `${text}\n\n[Attached files: ${fileNames}]`;
      setSessionFiles(prev => [...prev, ...currentFiles]);
    }

    const userMessage: ChatMessage = { role: 'user', content: fullText };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setUploadedFiles([]);
    setLoading(true);

    try {
      // Call the Senior Analyst endpoint (Grok-powered, page-aware, no tools)
      const response = await fetch(`${API_BASE}/senior-analyst/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          messages: newMessages,
          page_context: pageContext,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody.detail) detail = errBody.detail;
        } catch { /* ignore parse errors */ }
        throw new Error(detail);
      }

      const data = await response.json();
      let reply = data.reply || "I couldn't process that request. Please try again.";

      // Check for attestation completion marker (same logic as old chatbot)
      if (reply.includes('[ATTESTATION_COMPLETE]') && reply.includes('[ATTESTATION_DATA]')) {
        setAttestationSubmitted(true);
        // Clean the tags from the displayed reply
        reply = reply.replace('[ATTESTATION_COMPLETE]', '').trim();
        reply = reply.replace(/\[ATTESTATION_DATA\].*?\[\/ATTESTATION_DATA\]/s, '').trim();
      }

      const updatedMessages: ChatMessage[] = [...newMessages, { role: 'assistant' as const, content: reply }];
      setMessages(updatedMessages);
      const newSessionId = data.session_id || sessionId;
      if (data.session_id && !sessionId) {
        setSessionId(newSessionId);
      }
      persistMessages(updatedMessages, newSessionId, convId);
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Unknown error';
      const errorMessages: ChatMessage[] = [...newMessages, {
        role: 'assistant' as const,
        content: `The Senior Analyst encountered an error: ${errMsg}. Please try again.`,
      }];
      setMessages(errorMessages);
      persistMessages(errorMessages, sessionId, convId);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_BASE}/chatbot/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
          alert(`Failed to upload ${file.name}: ${err.detail || 'Unknown error'}`);
          continue;
        }
        const data = await response.json();
        setUploadedFiles(prev => [...prev, {
          filename: data.filename,
          original_name: data.original_name,
          url: data.url,
        }]);
      } catch {
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setConvId(generateConvId());
    setShowHistory(false);
    setAttestationSubmitted(false);
    setUploadedFiles([]);
    setSessionFiles([]);
    setVerificationRequired(false);
    setVerificationAttestationId(null);
    setVerificationStep('ask');
    setCompanyEmail('');
    setVerificationEmailUsed('');
  };

  const loadConversation = (conv: SavedConversation) => {
    setMessages(conv.messages);
    setSessionId(conv.session_id);
    setConvId(conv.id);
    setShowHistory(false);
  };

  const removeConversation = (cid: string) => {
    deleteConversation(cid);
    setAllConversations(prev => prev.filter(c => c.id !== cid));
    if (cid === convId) {
      startNewChat();
    }
  };

  const sendVerificationEmail = async (useCompanyEmail: boolean) => {
    if (!verificationAttestationId) return;
    setVerificationStep('sending');
    try {
      const body: { verification_email?: string } = {};
      if (useCompanyEmail && companyEmail.trim()) {
        body.verification_email = companyEmail.trim();
      }
      const resp = await fetch(
        `${API_BASE}/chatbot/attestations/${verificationAttestationId}/send-verification`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Failed to send' }));
        throw new Error(err.detail || 'Failed to send verification email');
      }
      const data = await resp.json();
      setVerificationEmailUsed(data.verification_email || '');
      setVerificationStep('sent');
    } catch (error) {
      setVerificationStep('ask');
      alert(error instanceof Error ? error.message : 'Failed to send verification email. Please try again.');
    }
  };

  return (
    <>
      {/* Floating Chat Button — BOTTOM RIGHT */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: 16,
          background: isOpen
            ? '#6B7280'
            : `linear-gradient(135deg, ${T.gold}, ${T.navy})`,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
          zIndex: 999,
          transition: 'all 0.2s',
        }}
        title={isOpen ? 'Close Senior Analyst' : 'Open Senior Analyst'}
      >
        {isOpen ? <IconX /> : <IconChat />}
      </button>

      {/* Chat Panel — BOTTOM RIGHT */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: 90,
          right: 24,
          width: 440,
          maxWidth: 'calc(100vw - 48px)',
          height: 620,
          maxHeight: 'calc(100vh - 120px)',
          background: T.white,
          borderRadius: 16,
          boxShadow: T.shadowLg,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 998,
          animation: 'chatSlideIn 0.3s ease-out',
          fontFamily: "'DM Sans', -apple-system, sans-serif",
        }}>

          {/* Header */}
          <div style={{
            padding: 16,
            borderBottom: `1px solid ${T.border}`,
            background: T.navy,
            borderRadius: '16px 16px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 8,
                background: T.gold,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconSparkles />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.white }}>
                  Senior Analyst
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  Pharmaceutical Intelligence
                </div>
              </div>
            </div>
            {/* Header actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                padding: '3px 8px', borderRadius: 6, fontSize: 10,
                fontWeight: 600, color: 'rgba(255,255,255,0.8)',
                background: 'rgba(255,255,255,0.1)',
                maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {pageContext.page_name}
              </span>
              <button
                onClick={() => setShowHistory(!showHistory)}
                title="Conversation history"
                style={{
                  padding: '4px 8px', borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: showHistory ? 'rgba(255,255,255,0.15)' : 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 11, cursor: 'pointer', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <IconHistory />
              </button>
              <button
                onClick={startNewChat}
                title="New conversation"
                style={{
                  padding: '4px 8px', borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent', color: 'rgba(255,255,255,0.7)',
                  fontSize: 11, cursor: 'pointer', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <IconPlus />
              </button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div style={{
              flex: 1, overflowY: 'auto', padding: 0,
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{
                padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <button
                  onClick={() => setShowHistory(false)}
                  style={{
                    padding: '4px 10px', borderRadius: 6,
                    border: `1px solid ${T.border}`, background: T.white,
                    fontSize: 12, cursor: 'pointer', fontWeight: 600,
                    color: T.textDark, display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <IconBack /> Back to chat
                </button>
                <span style={{ fontSize: 12, color: T.textMid, fontWeight: 600 }}>
                  {allConversations.length} conversation{allConversations.length !== 1 ? 's' : ''}
                </span>
              </div>
              {allConversations.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: T.textMid, fontSize: 13 }}>
                  No previous conversations yet.
                </div>
              ) : (
                allConversations.map(conv => {
                  const firstUserMsg = conv.messages.find(m => m.role === 'user');
                  const preview = firstUserMsg
                    ? firstUserMsg.content.slice(0, 80) + (firstUserMsg.content.length > 80 ? '...' : '')
                    : 'Empty conversation';
                  const date = new Date(conv.updated_at);
                  const timeStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                    + ' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                  const isCurrent = conv.id === convId;
                  return (
                    <div
                      key={conv.id}
                      style={{
                        padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
                        cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10,
                        background: isCurrent ? `${T.gold}08` : 'transparent',
                        transition: 'background 0.15s',
                      }}
                      onClick={() => loadConversation(conv)}
                      onMouseEnter={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = `${T.gold}05`; }}
                      onMouseLeave={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 11, fontWeight: 600, color: T.gold, marginBottom: 2,
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                          {conv.page_name}
                          {isCurrent && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 4,
                              background: T.gold, color: T.white,
                            }}>current</span>
                          )}
                        </div>
                        <div style={{
                          fontSize: 12, color: T.textDark, lineHeight: 1.4,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {preview}
                        </div>
                        <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>
                          {timeStr} &middot; {conv.messages.length} message{conv.messages.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); removeConversation(conv.id); }}
                        title="Delete conversation"
                        style={{
                          padding: 4, borderRadius: 4, border: 'none',
                          background: 'transparent', color: T.textLight,
                          cursor: 'pointer', flexShrink: 0, marginTop: 2,
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#EF4444'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = T.textLight; }}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Messages Area */}
          {!showHistory && (
          <div ref={scrollRef} style={{
            flex: 1, overflowY: 'auto', padding: 16,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {/* Empty state with quick actions */}
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: `linear-gradient(135deg, ${T.gold}20, ${T.navy}10)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2">
                    <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" />
                  </svg>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 4 }}>
                  Senior Analyst
                </div>
                <div style={{ fontSize: 12, color: T.textMid, marginBottom: 4 }}>
                  Viewing: {pageContext.page_name}
                </div>
                <div style={{ fontSize: 13, color: T.textMid, marginBottom: 20, lineHeight: 1.5 }}>
                  Ask about pharma data, product shortages, or self-attest a product claim.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(action.query)}
                      style={{
                        padding: '10px 14px', borderRadius: T.radiusSm,
                        border: `1px solid ${T.border}`, background: T.white,
                        fontSize: 13, fontWeight: 500, color: T.textDark,
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                      onMouseEnter={e => {
                        (e.target as HTMLButtonElement).style.borderColor = T.gold;
                        (e.target as HTMLButtonElement).style.background = `${T.gold}08`;
                      }}
                      onMouseLeave={e => {
                        (e.target as HTMLButtonElement).style.borderColor = T.border;
                        (e.target as HTMLButtonElement).style.background = T.white;
                      }}
                    >
                      {action.isAttestation ? (
                        <span style={{ color: T.gold, flexShrink: 0 }}><IconAttestation /></span>
                      ) : (
                        <span style={{ color: T.gold, fontSize: 14, flexShrink: 0 }}>&#9670;</span>
                      )}
                      {action.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user'
                    ? '12px 12px 4px 12px'
                    : '12px 12px 12px 4px',
                  background: msg.role === 'user' ? T.navy : T.offWhite,
                  color: msg.role === 'user' ? T.white : T.textDark,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}>
                  {msg.role === 'assistant'
                    ? renderMessage(msg.content)
                    : msg.content}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: '12px 12px 12px 4px',
                  background: T.offWhite, display: 'flex', gap: 4,
                }}>
                  {[0, 1, 2].map(j => (
                    <div key={j} style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: T.gold,
                      animation: `dotPulse 1.4s ease-in-out ${j * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}

            {/* Attestation success banner */}
            {attestationSubmitted && !verificationRequired && (
              <div style={{
                padding: '10px 14px',
                borderRadius: T.radiusSm,
                background: '#ECFDF5',
                border: '1px solid #BBF7D0',
                color: T.success,
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <IconAttestation />
                Attestation submitted for review. View it in the {user.is_admin ? 'Self-Attestations' : 'My Attestations'} page.
              </div>
            )}

            {/* Verification challenge UI */}
            {verificationRequired && (
              <div style={{
                padding: 16,
                borderRadius: T.radiusSm,
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                fontSize: 13,
                lineHeight: 1.6,
              }}>
                {verificationStep === 'ask' && (
                  <>
                    <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                      Identity Verification Required
                    </div>
                    <div style={{ color: '#78350F', marginBottom: 12 }}>
                      Your attestation for <strong>{verificationManufacturer}</strong> has been submitted.
                      To verify your identity and association with this company, we need to send you a verification email.
                    </div>
                    <div style={{ color: '#78350F', marginBottom: 12 }}>
                      Do you have a company email account associated with <strong>{verificationManufacturer}</strong>?
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setVerificationStep('email-input')}
                        style={{
                          padding: '8px 16px', borderRadius: 6, border: 'none',
                          background: T.gold, color: T.white, fontSize: 13,
                          fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        Yes, I have a company email
                      </button>
                      <button
                        onClick={() => sendVerificationEmail(false)}
                        style={{
                          padding: '8px 16px', borderRadius: 6,
                          border: `1px solid ${T.border}`, background: T.white,
                          color: T.textDark, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        No, use my email on file
                      </button>
                    </div>
                  </>
                )}

                {verificationStep === 'email-input' && (
                  <>
                    <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 8 }}>
                      Enter your company email
                    </div>
                    <div style={{ color: '#78350F', marginBottom: 12 }}>
                      Enter the email address associated with <strong>{verificationManufacturer}</strong>. We will send a verification link to this address.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="email"
                        value={companyEmail}
                        onChange={e => setCompanyEmail(e.target.value)}
                        placeholder="name@company.com"
                        style={{
                          flex: 1, padding: '8px 12px', borderRadius: 6,
                          border: `1px solid ${T.border}`, fontSize: 13,
                          fontFamily: "'DM Sans', -apple-system, sans-serif",
                          outline: 'none',
                        }}
                        onKeyPress={e => {
                          if (e.key === 'Enter' && companyEmail.trim()) {
                            sendVerificationEmail(true);
                          }
                        }}
                      />
                      <button
                        onClick={() => sendVerificationEmail(true)}
                        disabled={!companyEmail.trim() || !companyEmail.includes('@')}
                        style={{
                          padding: '8px 16px', borderRadius: 6, border: 'none',
                          background: companyEmail.trim() && companyEmail.includes('@') ? T.gold : '#E5E7EB',
                          color: T.white, fontSize: 13, fontWeight: 600,
                          cursor: companyEmail.trim() && companyEmail.includes('@') ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Send Verification
                      </button>
                    </div>
                    <button
                      onClick={() => setVerificationStep('ask')}
                      style={{
                        marginTop: 8, padding: 0, border: 'none', background: 'none',
                        color: T.textLight, fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
                      }}
                    >
                      Go back
                    </button>
                  </>
                )}

                {verificationStep === 'sending' && (
                  <div style={{ color: '#78350F', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #FDE68A', borderTopColor: '#92400E', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Sending verification email...
                  </div>
                )}

                {verificationStep === 'sent' && (
                  <>
                    <div style={{ fontWeight: 700, color: '#065F46', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                      Verification Email Sent!
                    </div>
                    <div style={{ color: '#047857' }}>
                      We sent a verification email to <strong>{verificationEmailUsed}</strong>.
                      Please check your inbox and click <strong>"Verify My Claim"</strong> to complete verification.
                      Once verified, an admin will be able to approve your attestation.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          )}

          {/* Uploaded files preview */}
          {!showHistory && uploadedFiles.length > 0 && (
            <div style={{
              padding: '8px 16px',
              borderTop: `1px solid ${T.border}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
            }}>
              {uploadedFiles.map((file, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 8px',
                  borderRadius: 6,
                  background: '#EFF6FF',
                  border: '1px solid #BFDBFE',
                  fontSize: 11,
                  color: '#1E40AF',
                  maxWidth: 180,
                }}>
                  <IconFile />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.original_name}
                  </span>
                  <button
                    onClick={() => removeFile(i)}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      color: '#6B7280',
                      fontSize: 14,
                      lineHeight: 1,
                      padding: 0,
                      marginLeft: 2,
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input Area */}
          {!showHistory && (
          <div style={{ padding: 16, borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* File upload button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || loading}
                style={{
                  padding: '10px',
                  borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`,
                  background: T.white,
                  color: uploading ? T.textLight : T.textMid,
                  cursor: uploading || loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                title="Attach evidence file"
              >
                {uploading ? (
                  <span style={{ fontSize: 12 }}>...</span>
                ) : (
                  <IconPaperclip />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => { if (e.key === 'Enter') sendMessage(); }}
                placeholder="Ask the Senior Analyst..."
                disabled={loading}
                style={{
                  flex: 1, padding: '10px 14px',
                  border: `2px solid ${T.border}`, borderRadius: T.radiusSm,
                  fontSize: 14, fontFamily: "'DM Sans', -apple-system, sans-serif",
                  outline: 'none', transition: 'border-color 0.2s',
                  color: T.textDark, background: T.white,
                }}
                onFocus={e => { e.target.style.borderColor = T.gold; }}
                onBlur={e => { e.target.style.borderColor = T.border; }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                style={{
                  padding: '10px 16px', borderRadius: T.radiusSm,
                  border: 'none',
                  background: input.trim() && !loading ? T.gold : '#E5E7EB',
                  color: T.white,
                  cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
              >
                <IconSend />
              </button>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes chatSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; }
          40% { opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
